import React from 'react';
import { Head, Link, useForm } from '@inertiajs/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ArrowLeft, Save, Hash, FileText, Code, Database, AlertCircle } from 'lucide-react';
import { Breadcrumbs } from '@/components/ui/breadcrumbs';

interface Document {
  id: number;
  name: string;
}

interface Chunk {
  id?: number;
  page_number?: number;
  citation?: string;
  content?: string;
  embed?: string;
  metadata?: Record<string, any>;
}

interface ChunkFormProps {
  document: Document;
  chunk: Chunk;
  isEdit: boolean;
  errors: Record<string, string[]>;
}

export default function ChunkForm({ document, chunk, isEdit, errors }: ChunkFormProps) {
  const { data, setData, post, put, processing, errors: formErrors } = useForm({
    page_number: chunk.page_number || '',
    citation: chunk.citation || '',
    content: chunk.content || '',
    embed: chunk.embed || '',
    metadata: chunk.metadata ? JSON.stringify(chunk.metadata, null, 2) : '',
  });

  const allErrors = { ...errors, ...formErrors };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Parse metadata JSON
    let metadataObj = {};
    if (data.metadata.trim()) {
      try {
        metadataObj = JSON.parse(data.metadata);
      } catch (error) {
        // Handle JSON parsing error - this should be caught by backend validation
      }
    }

    const formData = {
      chunk: {
        page_number: data.page_number,
        citation: data.citation,
        content: data.content,
        embed: data.embed,
        metadata: metadataObj,
      }
    };

    if (isEdit && chunk.id) {
      put(`/documents/${document.id}/chunks/${chunk.id}`, {
        data: formData,
        onSuccess: () => {
          // Handle success - Inertia will handle the redirect
        },
      });
    } else {
      post(`/documents/${document.id}/chunks`, {
        data: formData,
        onSuccess: () => {
          // Handle success - Inertia will handle the redirect
        },
      });
    }
  };

  const hasAnyErrors = Object.keys(allErrors).some(key => allErrors[key].length > 0);

  return (
    <>
      <Head title={`${isEdit ? 'Edit' : 'New'} Chunk - ${document.name}`} />
      
      <div className="container mx-auto p-6 max-w-4xl">
        <div className="space-y-6">
          {/* Breadcrumbs */}
          <Breadcrumbs opsConsole
            items={[
              { label: document.name, href: `/documents/${document.id}` },
              { label: 'Chunks', href: `/documents/${document.id}/chunks` },
              { label: isEdit ? 'Edit Chunk' : 'New Chunk', current: true }
            ]}
          />

          {/* Header */}
          <div className="flex items-center gap-4">
            <Button asChild variant="outline">
              <Link href={`/documents/${document.id}/chunks`}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Chunks
              </Link>
            </Button>
            <h1 className="text-3xl font-bold">
              {isEdit ? 'Edit Chunk' : 'New Chunk'} - {document.name}
            </h1>
          </div>

          {/* Error Summary */}
          {hasAnyErrors && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <div className="font-semibold mb-2">
                  {Object.keys(allErrors).reduce((count, key) => count + allErrors[key].length, 0)} error(s) found:
                </div>
                <ul className="list-disc list-inside space-y-1">
                  {Object.entries(allErrors).map(([field, fieldErrors]) =>
                    fieldErrors.map((error, index) => (
                      <li key={`${field}-${index}`}>{error}</li>
                    ))
                  )}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Chunk Details</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Page Number */}
                <div className="space-y-2">
                  <Label htmlFor="page_number" className="text-sm font-medium flex items-center gap-2">
                    <Hash className="h-4 w-4" />
                    Page Number
                  </Label>
                  <Input
                    id="page_number"
                    type="number"
                    value={data.page_number}
                    onChange={(e) => setData('page_number', e.target.value)}
                    className={allErrors.page_number ? 'border-red-500' : ''}
                    placeholder="Enter page number"
                  />
                  {allErrors.page_number && (
                    <div className="text-sm text-red-600">
                      {allErrors.page_number.join(', ')}
                    </div>
                  )}
                </div>

                {/* Citation */}
                <div className="space-y-2">
                  <Label htmlFor="citation" className="text-sm font-medium flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Citation
                  </Label>
                  <Input
                    id="citation"
                    type="text"
                    value={data.citation}
                    onChange={(e) => setData('citation', e.target.value)}
                    className={allErrors.citation ? 'border-red-500' : ''}
                    placeholder="Enter citation"
                  />
                  {allErrors.citation && (
                    <div className="text-sm text-red-600">
                      {allErrors.citation.join(', ')}
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="space-y-2">
                  <Label htmlFor="content" className="text-sm font-medium">
                    Content *
                  </Label>
                  <Textarea
                    id="content"
                    value={data.content}
                    onChange={(e) => setData('content', e.target.value)}
                    rows={8}
                    className={allErrors.content ? 'border-red-500' : ''}
                    placeholder="Enter chunk content..."
                    required
                  />
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>
                      {allErrors.content && (
                        <span className="text-red-600">{allErrors.content.join(', ')}</span>
                      )}
                    </span>
                    <span>{data.content.length} characters</span>
                  </div>
                </div>

                {/* Embed */}
                <div className="space-y-2">
                  <Label htmlFor="embed" className="text-sm font-medium flex items-center gap-2">
                    <Code className="h-4 w-4" />
                    Embed
                  </Label>
                  <Textarea
                    id="embed"
                    value={data.embed}
                    onChange={(e) => setData('embed', e.target.value)}
                    rows={8}
                    className={`font-mono text-xs ${allErrors.embed ? 'border-red-500' : ''}`}
                    placeholder="Enter embed data (typically vector representations)..."
                  />
                  {allErrors.embed && (
                    <div className="text-sm text-red-600">
                      {allErrors.embed.join(', ')}
                    </div>
                  )}
                </div>

                {/* Metadata */}
                <div className="space-y-2">
                  <Label htmlFor="metadata" className="text-sm font-medium flex items-center gap-2">
                    <Database className="h-4 w-4" />
                    Metadata (JSON)
                  </Label>
                  <Textarea
                    id="metadata"
                    value={data.metadata}
                    onChange={(e) => setData('metadata', e.target.value)}
                    rows={5}
                    className={`font-mono text-xs ${allErrors.metadata ? 'border-red-500' : ''}`}
                    placeholder='{"key": "value", "another_key": "another_value"}'
                  />
                  <div className="text-xs text-gray-600">
                    Enter valid JSON format for metadata
                  </div>
                  {allErrors.metadata && (
                    <div className="text-sm text-red-600">
                      {allErrors.metadata.join(', ')}
                    </div>
                  )}
                </div>

                {/* Submit Button */}
                <div className="flex justify-center pt-6">
                  <Button 
                    type="submit" 
                    disabled={processing}
                    size="lg"
                    className="px-8"
                  >
                    {processing ? (
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        {isEdit ? 'Updating...' : 'Creating...'}
                      </div>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        {isEdit ? 'Update Chunk' : 'Create Chunk'}
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
}